import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VerifiedUserOutlined, ArrowForward, HelpOutlineOutlined, CheckCircleOutline, CurrencyRupeeOutlined, WarningAmberOutlined, InfoOutlined, LightbulbOutlined } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const HowToCheckGoldPurityPage = () => {
    useEffect(() => {
        document.title = "How to Check Gold Purity in Rajasthan | BIS Hallmark Guide";

        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Learn how to check gold purity in Rajasthan using BIS hallmark standards. A complete guide to buying certified gold jewellery safely.");
        }
    }, []);

    return (
        <div className="page-container seo-page">
            <div className="seo-content-wrapper">
                <h1>How to Check Gold Purity in Rajasthan (BIS Hallmark Guide)</h1>

                <p className="seo-intro">
                    Buying gold jewellery is a significant investment, especially in Rajasthan where gold holds cultural, emotional, and financial value. Whether you are purchasing gold for weddings, festivals, or long-term investment, checking gold purity is essential.
                </p>

                <p>
                    At <strong>MAB Jewels</strong>, we believe every customer should understand how to verify gold purity before making a purchase. This guide explains BIS hallmarking, purity standards, and simple ways to ensure you are buying certified gold jewellery in Rajasthan.
                </p>

                <h2>What Is Gold Purity?</h2>
                <p>
                    Gold purity refers to the percentage of pure gold present in jewellery or coins. Common gold purities in India:
                </p>
                <ul className="seo-check-list">
                    <li><strong>24K Gold</strong> – 99.9% pure (used for coins & bars)</li>
                    <li><strong>22K Gold</strong> – 91.6% pure (used for jewellery)</li>
                    <li><strong>18K Gold</strong> – 75% pure (used for designer jewellery)</li>
                </ul>
                <div className="seo-highlight-box">
                    <p><InfoOutlined sx={{ color: '#d4af37', verticalAlign: 'middle', mr: 1 }} /> <strong>Note:</strong> Most jewellery in Rajasthan is made using 22K gold, as it offers the right balance of purity and durability.</p>
                </div>

                <h2>What Is BIS Hallmark Gold?</h2>
                <p>
                    The Bureau of Indian Standards (BIS) is the official authority that certifies gold purity in India. BIS hallmark gold jewellery ensures:
                </p>
                <ul className="seo-check-list">
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Guaranteed purity</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Legal compliance</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Consumer protection</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Trust & transparency</li>
                </ul>
                <p>Buying BIS hallmark gold protects customers from under-karat gold and unfair pricing.</p>

                <h2>BIS Hallmark Symbols Explained (Very Important)</h2>
                <p>Every BIS hallmarked gold jewellery piece must contain 4 mandatory marks:</p>
                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #e0e0e0', margin: '20px 0' }}>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '30px', color: '#d4af37' }}>1.</strong> BIS Logo</li>
                        <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '30px', color: '#d4af37' }}>2.</strong> Purity Mark (e.g., 22K916, 18K750)</li>
                        <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '30px', color: '#d4af37' }}>3.</strong> Assaying Centre Mark</li>
                        <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '30px', color: '#d4af37' }}>4.</strong> Jeweller's Identification Mark</li>
                    </ul>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#d32f2f', fontWeight: 'bold' }}><WarningAmberOutlined sx={{ verticalAlign: 'middle', mr: 1 }} /> If any of these are missing, the jewellery is not BIS certified.</p>
                </div>

                <h2>How to Check Gold Purity in Rajasthan (Step-by-Step)</h2>

                <div className="seo-steps-list">
                    <h3>1. Look for BIS Hallmark Stamp</h3>
                    <p>Always check the inner surface of rings, bangles, or chains for BIS hallmark symbols.</p>

                    <h3>2. Verify Purity Code</h3>
                    <ul style={{ listStyle: 'none', paddingLeft: '20px' }}>
                        <li><strong>22K</strong> → 916</li>
                        <li><strong>18K</strong> → 750</li>
                        <li><strong>24K</strong> → 999</li>
                    </ul>

                    <h3>3. Ask for BIS Certificate / Invoice</h3>
                    <p>A trusted jeweller will always provide a proper invoice mentioning purity and weight.</p>

                    <h3>4. Visit a BIS Assaying Centre (Optional)</h3>
                    <p>For complete assurance, you can get jewellery tested at a BIS-authorized hallmarking centre.</p>
                </div>

                <h2>How to Avoid Fake or Low-Purity Gold</h2>
                <ul className="seo-check-list" style={{ listStyle: 'none' }}>
                    <li style={{ color: '#d32f2f' }}>🚫 Avoid jewellery without hallmark</li>
                    <li style={{ color: '#d32f2f' }}>🚫 Avoid unclear pricing</li>
                    <li style={{ color: '#d32f2f' }}>🚫 Avoid cash-only sellers without invoices</li>
                </ul>
                <div className="seo-highlight-box" style={{ background: '#fff8e1', borderLeft: '4px solid #d4af37' }}>
                    <p><LightbulbOutlined sx={{ color: '#d4af37', verticalAlign: 'middle', mr: 1 }} /> <strong>Tip:</strong> Always buy from a trusted, established jeweller with a physical store.</p>
                </div>

                <h2>Why Buy Certified Gold Jewellery from MAB Jewels?</h2>
                <ul className="seo-check-list">
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> BIS Hallmark certified gold</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Transparent pricing aligned with live gold rates</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Trusted legacy of Manoj Abhushan Bhandar</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Serving customers across Rajasthan with honesty & craftsmanship</li>
                </ul>
                <p style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '15px' }}>
                    <VerifiedUserOutlined sx={{ color: '#d4af37' }} /> Visit MAB Jewels for certified gold jewellery you can trust.
                </p>

                <h2>FAQs – Gold Purity in Rajasthan</h2>
                <div className="seo-faq-section">
                    <div className="seo-faq-item">
                        <h4>Q1. Is BIS hallmark mandatory in Rajasthan?</h4>
                        <p>Yes. BIS hallmarking is mandatory for gold jewellery sold in India.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q2. Is 22K gold best for jewellery?</h4>
                        <p>Yes. 22K gold is ideal for jewellery due to durability and high purity.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q3. Can gold purity differ between cities?</h4>
                        <p>Purity does not differ, but making charges and margins may vary between cities like Jodhpur, Jaipur, and Udaipur.</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/gold-jewellery-jodhpur">Gold Jewellery in Jodhpur</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/wedding-jewellery-rajasthan">Wedding Jewellery in Rajasthan</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/gold-rate-rajasthan">Today Gold Rate in Rajasthan</Link></li>
                    </ul>
                    <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>
                            Explore more gold insights at <Link to="/gold-guide" style={{ color: '#d4af37', fontWeight: 'bold', textDecoration: 'none' }}>Gold Guide by MAB Jewels</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};



export default HowToCheckGoldPurityPage;
